/**
 * Options for the `diffDOM` function.
 */
type DiffOptions = {
    /**
     * If `true`, restores focus to the currently focused element after DOM update.
     */
    preserveFocus?: boolean;
    /**
     * If `true`, allows diffing and applying HTML strings with multiple root elements.
     * When enabled, the entire container content will be replaced instead of diffed node-by-node.
     * Use this when `newHtmlString` contains sibling elements without a common parent.
     */
    wrapMultipleRoots?: boolean;
};

/**
 * Diffs the existing DOM inside the given container with new HTML markup and applies minimal changes.
 * If the container has no child, the new HTML content is appended directly.
 *
 * @param {Element} container - The DOM element whose content should be updated.
 * @param {string} newHtmlString - The new HTML markup to apply.
 * @param {DiffOptions} [options={}] - Optional settings to control diff behavior.
 */
export function diffDOM(container: Element, newHtmlString: string, options: DiffOptions = {}) {
    const { preserveFocus = false, wrapMultipleRoots = false } = options;

    const template = document.createElement('template');
    template.innerHTML = newHtmlString.trim();

    const fragment = template.content;

    const active = preserveFocus ? (document.activeElement as HTMLElement) : null;

    if (wrapMultipleRoots) {
        container.innerHTML = '';
        container.appendChild(fragment.cloneNode(true));
    } else {
        const newNode = fragment.firstElementChild;
        if (!newNode) return;

        if (container.firstElementChild) {
            patch(container.firstElementChild, newNode);
        } else {
            container.appendChild(newNode.cloneNode(true));
        }
    }

    if (preserveFocus && active && document.body.contains(active)) {
        active.focus();
    }
}

/**
 * Recursively patches an old DOM node to match a new one by updating only what is necessary.
 *
 * @param {Node} oldNode - The existing DOM node.
 * @param {Node} newNode - The new DOM node to match.
 */
function patch(oldNode: Node, newNode: Node) {
    if (oldNode.nodeType !== newNode.nodeType) {
        (oldNode as ChildNode).replaceWith(newNode.cloneNode(true));
        return;
    }

    if (oldNode.nodeType === Node.TEXT_NODE) {
        if (oldNode.textContent !== newNode.textContent) {
            oldNode.textContent = newNode.textContent;
        }
        return;
    }

    const oldEl = oldNode as Element;
    const newEl = newNode as Element;

    if (oldEl.nodeName !== newEl.nodeName) {
        oldEl.replaceWith(newEl.cloneNode(true));
        return;
    }

    updateAttributes(oldEl, newEl);
    patchChildren(oldEl, newEl);
}

/**
 * Updates the attributes of an existing element to match those of a new element.
 *
 * @param {Element} oldEl - The element whose attributes will be updated.
 * @param {Element} newEl - The element whose attributes will be used as reference.
 */
function updateAttributes(oldEl: Element, newEl: Element) {
    const oldAttrs = oldEl.attributes;
    const newAttrs = newEl.attributes;

    // Remove attributes that no longer exist
    for (let i = oldAttrs.length - 1; i >= 0; i--) {
        const attr = oldAttrs[i];
        if (!newEl.hasAttribute(attr.name)) {
            oldEl.removeAttribute(attr.name);
        }
    }

    // Add new attributes or update existing ones
    for (let i = 0; i < newAttrs.length; i++) {
        const attr = newAttrs[i];
        if (oldEl.getAttribute(attr.name) !== attr.value) {
            oldEl.setAttribute(attr.name, attr.value);
        }
    }

    // Sync specific form element properties
    if (oldEl instanceof HTMLInputElement && newEl instanceof HTMLInputElement) {
        oldEl.checked = newEl.checked;
        oldEl.value = newEl.value;
    } else if (oldEl instanceof HTMLTextAreaElement && newEl instanceof HTMLTextAreaElement) {
        oldEl.value = newEl.value;
    } else if (oldEl instanceof HTMLSelectElement && newEl instanceof HTMLSelectElement) {
        oldEl.value = newEl.value;
    }
}

/**
 * Diffs and updates the child nodes of a given element.
 *
 * @param {Element} oldEl - The existing DOM element whose children will be patched.
 * @param {Element} newEl - The new DOM element to use as a reference for children.
 */
function patchChildren(oldEl: Element, newEl: Element) {
    const oldChildren = Array.from(oldEl.childNodes);
    const newChildren = Array.from(newEl.childNodes);
    const max = Math.max(oldChildren.length, newChildren.length);

    for (let i = 0; i < max; i++) {
        const oldChild = oldChildren[i];
        const newChild = newChildren[i];

        if (!oldChild && newChild) {
            oldEl.appendChild(newChild.cloneNode(true));
        } else if (oldChild && !newChild) {
            oldEl.removeChild(oldChild);
        } else if (oldChild && newChild) {
            patch(oldChild, newChild);
        }
    }
}
